import { Decorative } from "@faire/design-tokens";
import { formatMoney } from "@faire/web/common/money";
import { Exclusivity as ExclusivityIcon } from "@faire/web/slate/icons/foundation/Exclusivity";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import differenceInDays from "date-fns/differenceInDays";
import React from "react";

import { ExclusivityLink } from "@faire/retailer/features/Exclusivity/DetailsModal/ExclusivityLink";
import { getStateInfoFromAvailability } from "@faire/retailer/features/Exclusivity/getStateInfoFromAvailability";
import { useExclusivityAvailability } from "@faire/retailer/hooks/exclusivity/useExclusivityAvailability";

interface IProps {
  brandToken: string;
}

export const ExclusivityLabel: React.FC<IProps> = ({ brandToken }) => {
  const { availability } = useExclusivityAvailability(brandToken);
  const { isActivated } = getStateInfoFromAvailability(availability);

  const {
    commitment_balance_left_to_spend,
    expires_at,
    exclusivity_agreement_token,
  } = availability ?? {};

  const balanceLeftString = commitment_balance_left_to_spend
    ? formatMoney({
        money: commitment_balance_left_to_spend,
        omitCentsIfZero: true,
      })
    : "";
  const daysToExpire = differenceInDays(expires_at ?? 0, Date.now());
  const showBalanceLeft =
    commitment_balance_left_to_spend?.amount_cents &&
    commitment_balance_left_to_spend.amount_cents > 0;
  const showExpiration = !showBalanceLeft && daysToExpire <= 90;

  if (!isActivated) {
    return null;
  }

  return (
    <>
      <Spacer height="3x" />
      <Row gap={4}>
        <ExclusivityIcon
          color={Decorative.shade.orange}
          fontSize="16px"
          margin="2px 0 0 0"
        />
        <ExclusivityLink
          origin="product"
          agreementToken={exclusivity_agreement_token}
          brandToken={brandToken}
        />
        {showBalanceLeft ? (
          <Typography>
            <StrictLocalMsg
              defaultMessage="{balanceLeftMoney} left to spend"
              description={{
                text: "balance left to spend for the exclusivity agreement",
                img: "https://cdn.faire.com/fastly/f9be4b99ed53dbaf6fcfaf95cfcd2e1461e3a23b25fc52ca20c9095cb9212f7e.png",
              }}
              values={{ balanceLeftMoney: balanceLeftString }}
            />
          </Typography>
        ) : null}
        {showExpiration ? (
          <Typography>
            <StrictLocalMsg
              defaultMessage="ends in {daysToExpireNumber} days"
              description={{
                text: "number of days remaining before the exclusivity agreement gets expired",
                img: "https://cdn.faire.com/fastly/a0305e967586c1be5d943509d63474f3a30c4ae6747f529b2a6a422f191c0b84.png",
              }}
              values={{ daysToExpireNumber: daysToExpire }}
            />
          </Typography>
        ) : null}
      </Row>
    </>
  );
};
